import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Btn } from "../../components/UI";
import { SlidePanel } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect } from "./FormControls";
import { DateField } from "./DateField";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TONE = { urgent: "bg-[#FFEBEE] text-[#C62828]", high: "bg-[#FFF3E0] text-[#B84A00]", medium: "bg-[#FFF8E1] text-[#876600]", low: "bg-[#E8F5E9] text-[#2E7D32]" };

export function TemplatePanel({ open, onClose, staff, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState("");
  const [anchor, setAnchor] = useState(dayjs().format("YYYY-MM-DD"));
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/intake-task-templates").then((r) => setTemplates(r.data)).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [open]);

  const tpl = templates.find((t) => t.key === sel);

  const apply = async () => {
    if (!tpl || !anchor) return;
    setBusy(true);
    try {
      const r = await api.post(`/intake-task-templates/${tpl.key}/apply`, { anchor_date: anchor, client_id: clientId, client_name: clientName, assignee_id: assignee });
      toast.success(`${r.data.created} intake tasks created from ${r.data.template}`);
      onApplied(); onClose();
      setSel(""); setClientId(""); setClientName(""); setAssignee("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <SlidePanel open={open} onClose={onClose} title="Apply Compliance Template"
      subtitle="Enroll and onboard with a preset state compliance checklist. Every task gets a target completion date." testId="template-panel">
      <div className="space-y-4">
        <div>
          <FLabel>1. Choose a compliance template</FLabel>
          <div className="mt-1 space-y-2">
            {templates.map((t) => (
              <button key={t.key} type="button" data-testid={`template-card-${t.key}`} onClick={() => setSel(t.key)}
                className={`w-full rounded-xl border p-3 text-left transition-colors ${sel === t.key ? "border-[#4E342E] bg-[#F5F2EB]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"}`}>
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-[#2E7D32]" />
                  <span className="text-sm font-bold text-[#3E2723]">{t.name}</span>
                  <span className="rounded-full bg-[#4E342E] px-2 py-0.5 text-[10px] font-bold text-white">{t.state}</span>
                  <span className="ml-auto text-[11px] font-semibold text-[#7B6157]">{t.items.length} tasks</span>
                </div>
                <div className="mt-1 text-[11px] leading-snug text-[#7B6157]">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        {tpl && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FLabel>Enrollment date *</FLabel>
                <DateField testId="template-anchor-date" value={anchor} onChange={setAnchor} />
              </div>
              <div>
                <FLabel>Assign all tasks to</FLabel>
                <FSelect data-testid="template-assignee-select" placeholder="Unassigned" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  {staff.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </FSelect>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FLabel>Link to client record</FLabel>
                <FSelect data-testid="template-client-select" placeholder="No linked client" value={clientId} onChange={(e) => { setClientId(e.target.value); if (e.target.value) setClientName(""); }}>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </FSelect>
              </div>
              <div>
                <FLabel>Or client name (free text)</FLabel>
                <FInput data-testid="template-client-input" value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={!!clientId} placeholder="e.g., Jordan Michaels" />
              </div>
            </div>
            <div>
              <FLabel>2. Preview target dates</FLabel>
              <div className="mt-1 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-[#EFEBE0] p-2" data-testid="template-preview">
                {tpl.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-[#FDFCF8]">
                    <span className="w-12 shrink-0 rounded bg-[#EFEBE0] px-1.5 py-0.5 text-center text-[10px] font-bold text-[#6D4C41]">Day {it.offset}</span>
                    <span className="w-16 shrink-0 font-semibold tabular-nums text-[#5D4037]">{dayjs(anchor).add(it.offset, "day").format("MMM D")}</span>
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${PRIORITY_TONE[it.priority]}`}>{it.priority}</span>
                    <span className="text-[#3E2723]">{it.title}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <Btn data-testid="template-apply-btn" disabled={busy || !anchor} onClick={apply}>
                {busy ? "Creating…" : `Create ${tpl.items.length} Tasks`}
              </Btn>
            </div>
          </>
        )}
      </div>
    </SlidePanel>
  );
}
